import * as APITypes from "~/API";
import { apiClient } from "~/services/apiClient";

export const EMPTY_USER_INFO: APITypes.User = {
  __typename: "User",
  id: "",
  createdAt: "",
  updatedAt: "",
  roles: APITypes.ManagerRolesType.MaintenanceStaff,
  _version: 0,
  _lastChangedAt: 0,
};

export interface FirestoreUserProfile {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  updatedAt: string;
  profilePictureUrl?: string;
}

export interface UserLoaderResult {
  user: APITypes.User | null;
  profile: FirestoreUserProfile | null;
}

export type UserInfoWithProfileResult = {
  success: boolean;
  data: APITypes.User;
  profile: FirestoreUserProfile | null;
  error?: unknown;
};

/**
 * Get user info using Lambda API loader endpoint.
 * This is the preferred method for fetching user data as it uses the Lambda API
 * instead of Amplify GraphQL, ensuring consistency with other API calls.
 */
export const getUserInfoWithLambda = async ({
  userID,
}: {
  userID?: string;
}): Promise<UserInfoWithProfileResult> => {
  if (!userID) {
    return {
      success: false,
      data: EMPTY_USER_INFO,
      profile: null,
      error: "userID is required",
    };
  }

  try {
    const result = await apiClient.loader<UserLoaderResult>("user", {
      id: userID,
    });

    if (!result.success || !result.data) {
      return {
        success: false,
        data: EMPTY_USER_INFO,
        profile: null,
        error: result.error || "Failed to fetch user",
      };
    }

    const user = result.data.user;
    if (!user?.id) {
      return {
        success: false,
        data: EMPTY_USER_INFO,
        profile: null,
        error: "User not found",
      };
    }

    return {
      success: true,
      data: user,
      profile: result.data.profile ?? null,
    };
  } catch (error) {
    return {
      success: false,
      data: EMPTY_USER_INFO,
      profile: null,
      error,
    };
  }
};
